import { useOPMFDTs } from "@/api/opm-fdt";
import { useDownloadOPMZipByRFS, useRFS, useRFSStatus } from "@/api/rfs";
import { Checking, ContainerPrimary, ContainerSecondary } from "../ui";
import { DownloadButton } from "@/components/ui/button";
import { Anchor } from "@/components/ui/anchor";
import { P } from "@/components/ui/typography";
import { RequiredAsterisk } from "@/components/form/required-asterisk";
import { formatDateTime } from "@/utils/helper/formatDate";
import { DataInfoWithBadge } from "@/components/ui/data-info";
import { useRegeneratePolling } from "../../hooks/useRegeneratePolling";

export function OPMHandler({
  id,
  hasRequiredAsterisk = false,
}: {
  id: string;
  hasRequiredAsterisk?: boolean;
}) {
  const { data: rfs } = useRFS({ id });
  const { data: rfsStatus } = useRFSStatus({ id });
  const { currentStatus } = useRegeneratePolling({ id });
  const { data: opmFdts, isPending: isLoadingOpmFdts } = useOPMFDTs({
    rfsId: id,
  });

  const { mutate: downloadOPMZip, isPending: isDownloadingOPMZip } =
    useDownloadOPMZipByRFS();

  if ((opmFdts ?? []).length > 1) {
    return <P>There are more than one OPM FDT</P>;
  }

  function handleDownloadOPMZip() {
    downloadOPMZip({ id, name: `ZIP-${rfs?.clusterId}.zip` });
  }

  const opmStatus = rfsStatus?.opmStatus ?? rfs?.opmStatus;
  const isValidated = opmStatus === "Validated";
  const shouldShowCreateButton = currentStatus == "SUCCESS";

  return (
    <ContainerPrimary>
      <span className="flex-grow whitespace-nowrap">
        OPM{hasRequiredAsterisk && <RequiredAsterisk />}
      </span>
      <ContainerSecondary className="w-fit flex-none gap-x-4">
        {isLoadingOpmFdts ? (
          <Checking />
        ) : (
          <>
            <div className="flex flex-col items-center text-center text-xs">
              <span className="text-center font-medium">
                Doc Uncompleted Date
              </span>
              <span className="text-center">
                {rfs?.opmUncompletedDate
                  ? formatDateTime(rfs?.opmUncompletedDate)
                  : "-"}
              </span>
            </div>
            <div className="flex flex-col items-center text-center text-xs">
              <span className="text-center font-medium">
                Doc Completed Date
              </span>
              <span className="text-center">
                {rfs?.opmCompletedDate
                  ? formatDateTime(rfs?.opmCompletedDate)
                  : "-"}
              </span>
            </div>
            <DataInfoWithBadge
              value={
                rfs?.opmIsSubmitted === true ? "Submitted" : "Not Submitted"
              }
            />
            <DataInfoWithBadge value={opmStatus ?? "Unvalidated"} />
            {isValidated && (
              <DownloadButton
                onClick={handleDownloadOPMZip}
                loading={isDownloadingOPMZip}
              />
            )}
            <Anchor
              mood="axiata-blue-outline"
              href={`/rfs/rfs/${id}/opm/create`}
              state={{ from: `/rfs/rfs/${id}?tab=rfs-process` }}
              className={shouldShowCreateButton ? "visible" : "invisible"}
            >
              {!isValidated ? <p>Create</p> : <p>Detail</p>}
            </Anchor>
          </>
        )}
      </ContainerSecondary>
    </ContainerPrimary>
  );
}
